import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { withCurrentLocalePath } from '../../utils/localePath';

const LOCALES = ['en', 'it', 'es', 'fr', 'de'] as const;

const stripLocalePrefix = (pathname: string) => pathname.replace(/^\/(en|it|es|fr|de)(?=\/|$)/, '') || '/';

const languagePath = (locale: string, pathname: string) => {
  const cleanPath = stripLocalePrefix(pathname);
  return cleanPath === '/' ? `/${locale}` : `/${locale}${cleanPath}`;
};

const authRoutePattern = /^\/(en|it|es|fr|de)?\/?(login|signup|confirm-signup|forgot-password|reset-password|force-new-password)(?=\/|$)/;

const AppFooter: React.FC = () => {
  const { t } = useTranslation('common');
  const location = useLocation();

  if (authRoutePattern.test(location.pathname)) {
    return null;
  }

  const linkGroups = [
    {
      title: t('footer.sections.explore'),
      links: [
        { label: t('navigation.home'), to: '/' },
        { label: t('navigation.now'), to: '/whats-on-now' },
        { label: t('navigation.guide'), to: '/codex/' },
        { label: t('navigation.conditions'), to: '/toolbox' },
      ],
    },
    {
      title: t('footer.sections.guide'),
      links: [
        { label: t('footer.links.species'), to: '/codex/species' },
        { label: t('footer.links.techniques'), to: '/codex/techniques' },
        { label: t('footer.links.rigs'), to: '/codex/rigs' },
        { label: t('footer.links.spots'), to: '/codex/locations' },
        { label: t('footer.links.setups'), to: '/codex/setup' },
      ],
    },
    {
      title: t('footer.sections.trust'),
      links: [
        { label: t('footer.links.terms'), to: '/terms' },
        { label: t('footer.links.privacy'), to: '/privacy' },
        { label: t('footer.links.cookies'), to: '/cookies' },
        { label: t('footer.links.affiliateDisclosure'), to: '/affiliate-disclosure' },
        { label: t('footer.links.legalInfo'), to: '/legal-info' },
      ],
    },
  ];

  return (
    <footer className="relative z-10 border-t border-[#90e0ef]/70 bg-white/95 px-4 pb-28 pt-8 text-[#03045e] sm:px-6 lg:px-8 lg:pb-10">
      <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.35fr_2fr]">
        <div className="max-w-md space-y-4">
          <Link to={withCurrentLocalePath('/', location.pathname)} className="inline-flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary-500 text-lg text-white shadow-sm">🌊</span>
            <span>
              <span className="block font-display text-xl font-semibold leading-none">{t('app.title')}</span>
              <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.16em] text-primary-600">{t('app.taglineShort')}</span>
            </span>
          </Link>
          <p className="text-sm leading-6 text-[#0077b6]">{t('footer.description')}</p>
          <p className="text-xs leading-5 text-[#3a5580]">{t('footer.affiliateNote')}</p>
        </div>

        <div className="grid gap-7 sm:grid-cols-3">
          {linkGroups.map((group) => (
            <nav key={group.title} aria-label={group.title} className="space-y-3">
              <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-600">{group.title}</h2>
              <ul className="space-y-2">
                {group.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={withCurrentLocalePath(link.to, location.pathname)}
                      className="text-sm font-medium text-[#03045e] transition-colors hover:text-primary-600"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-8 flex max-w-6xl flex-col gap-4 border-t border-[#caf0f8] pt-5 text-xs text-[#3a5580] sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          <a
            href="https://fenutech.com/"
            className="rounded-sm font-semibold text-[#03045e] underline decoration-[#90e0ef] decoration-2 underline-offset-4 transition-colors hover:text-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            {t('footer.fenutechCredit')}
          </a>
        </div>
        <nav aria-label={t('footer.languageNav')} className="flex flex-wrap items-center gap-2">
          {LOCALES.map((locale) => (
            <Link
              key={locale}
              to={languagePath(locale, location.pathname)}
              lang={locale}
              className="rounded-full border border-[#90e0ef] px-3 py-1 font-semibold uppercase tracking-[0.08em] text-[#03045e] transition-colors hover:bg-[#e9fbff]"
            >
              {locale}
            </Link>
          ))}
        </nav>
      </div>
    </footer>
  );
};

export default AppFooter;
